import { createClient } from '@supabase/supabase-js';
// First, so the recovery/invite link type is read before the client strips the URL hash.
import { markPasswordRecovery } from '@/shared/lib/auth-link-type';

const supabaseUrl: string | undefined = import.meta.env.VITE_SUPABASE_URL || undefined;
const supabaseAnonKey: string | undefined = import.meta.env.VITE_SUPABASE_ANON_KEY || undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '[UniPostudio] Missing Supabase env vars. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
  );
}

/**
 * supabase-js throws "supabaseUrl is required" from `createClient('')`, so a
 * build without the env var crashed at import and rendered a blank page
 * instead of the warning above. An unroutable placeholder keeps the module
 * loadable; every auth call then fails like a network error would.
 */
const MISSING_URL_PLACEHOLDER = 'http://supabase-url-missing.invalid';
const MISSING_KEY_PLACEHOLDER = 'supabase-anon-key-missing';

/**
 * Clear stale Supabase auth tokens from any OTHER project ref. When we
 * migrate Supabase projects (or rotate them), old `sb-<oldref>-auth-token`
 * keys linger in localStorage. Supabase's autoRefreshToken can sometimes
 * pick them up and trigger unexpected SIGNED_OUT events — exactly what
 * causes "dashboard appears, then bounces back to /auth" loops on the
 * first login after a project migration.
 */
function clearStaleSupabaseStorage(): void {
  if (typeof localStorage === 'undefined') return;
  if (!supabaseUrl) return;
  const currentRef = supabaseUrl.match(/^https:\/\/([^.]+)\./)?.[1];
  if (!currentRef) return;
  const currentKey = `sb-${currentRef}-auth-token`;
  const stale: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    // Match Supabase auth token keys for any other project ref
    if (/^sb-[a-z0-9]+-auth-token(\.[\w-]+)?$/.test(key) && !key.startsWith(currentKey)) {
      stale.push(key);
    }
  }
  for (const key of stale) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
  if (stale.length > 0) {
    console.warn(
      `[UniPostudio] Cleared ${stale.length} stale Supabase token(s) from another project ref:`,
      stale,
    );
  }
}

clearStaleSupabaseStorage();

/** The project URL and anon key the client was built with (placeholders when unset). */
export const SUPABASE_URL = supabaseUrl ?? MISSING_URL_PLACEHOLDER;
export const SUPABASE_ANON_KEY = supabaseAnonKey ?? MISSING_KEY_PLACEHOLDER;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

/**
 * In-memory access-token cache. The HTTP interceptor previously called
 * `supabase.auth.getSession()` on every request, which can block on
 * Supabase's internal auth lock during token refresh — adding seconds
 * of latency to every API call after a refresh fires. Subscribing to
 * onAuthStateChange and caching the token in module scope means the
 * interceptor reads a synchronous variable instead.
 *
 * Reference: https://github.com/supabase/auth-js/issues/762
 */
let cachedAccessToken: string | null = null;
/** When the cached token expires, in epoch seconds (Supabase's `expires_at`). */
let cachedExpiresAt: number | null = null;

/** Refresh this long before expiry: a token that dies in flight still 401s. */
const EXPIRY_MARGIN_SECONDS = 30;

function cacheSession(session: { access_token: string; expires_at?: number } | null): void {
  cachedAccessToken = session?.access_token ?? null;
  cachedExpiresAt = session?.expires_at ?? null;
}

/**
 * The cached token while it is still good. Null when there is none, or when
 * it expires within the margin (a laptop that slept past the hour wakes with
 * a dead token): the caller then asks the SDK, which refreshes it.
 */
export function getCachedAccessToken(nowSeconds: number = Date.now() / 1000): string | null {
  if (!cachedAccessToken) return null;
  if (cachedExpiresAt !== null && cachedExpiresAt - EXPIRY_MARGIN_SECONDS <= nowSeconds) return null;
  return cachedAccessToken;
}

void supabase.auth.getSession().then(({ data }) => {
  cacheSession(data.session);
}).catch(() => {
  cacheSession(null);
});

supabase.auth.onAuthStateChange((event, session) => {
  cacheSession(session);
  if (event === 'PASSWORD_RECOVERY') markPasswordRecovery();
});

let refreshing: Promise<string | null> | null = null;

/**
 * One token refresh for every request that 401'd at the same time. Resolves
 * to the new access token, or null when the session can't be refreshed (the
 * refresh token was revoked or the user signed out).
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = supabase.auth
      .refreshSession()
      .then(({ data, error }) => {
        if (error || !data.session) return null;
        cacheSession(data.session);
        return data.session.access_token;
      })
      .catch(() => null)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}
